import { useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useMaintenance } from '../../contexts/MaintenanceContext';
import { subscribeRealtime } from '../../services/realtime';
import { OFFLINE_TASBEEH_SYNCED } from '../../services/offlineTasbeehStorage';
import { acceptTasbeehUpdate, flushTasbeehCounter, TASBEEH_CHANGED, TASBEEH_STORAGE_SIGNAL } from '../../services/tasbeehCounter';

// Lives outside pages: leaving the counter cannot drop an unacknowledged tap.
export default function TasbeehSync() {
  const { currentUserId, loading } = useAuth();
  const { online, blocked, error, checking } = useMaintenance();
  useEffect(() => {
    if (!currentUserId || loading) return undefined;
    const controller = new AbortController();
    let timer, running = false, retryDelay = 2000;
    const allowed = online && !error && !checking && !blocked;
    const schedule = (delay = 850) => { if (allowed && !timer && !controller.signal.aborted) timer = setTimeout(run, delay); };
    async function run() {
      timer = null;
      if (running || !allowed || controller.signal.aborted) return;
      running = true;
      try { await flushTasbeehCounter(currentUserId, controller.signal); retryDelay = 2000; }
      catch { schedule(retryDelay); retryDelay = Math.min(30000, retryDelay * 2); }
      finally { running = false; }
    }
    const changed = event => { if (event.detail?.accountId === currentUserId) schedule(); };
    const storage = event => {
      if (event.key !== TASBEEH_STORAGE_SIGNAL) return;
      try { if (JSON.parse(event.newValue)?.accountId === currentUserId) schedule(); } catch {}
    };
    const offlineSynced = event => {
      if (event.detail?.accountId === currentUserId) void acceptTasbeehUpdate(currentUserId, event.detail).catch(() => {});
    };
    const visible = () => { if (!document.hidden) schedule(0); };
    const stop = subscribeRealtime(event => {
      if (['tasbeeh.updated','tasbeeh.offline-synced','tasbeeh.goal-changed'].includes(event.type) && event.data?.accountId === currentUserId) {
        void acceptTasbeehUpdate(currentUserId, event.data).catch(() => {});
      }
      if (['ready','reconnected','resync'].includes(event.type)) schedule(0);
    });
    schedule(0);
    window.addEventListener(TASBEEH_CHANGED, changed);
    window.addEventListener('storage', storage);
    window.addEventListener(OFFLINE_TASBEEH_SYNCED, offlineSynced);
    document.addEventListener('visibilitychange', visible);
    return () => { controller.abort(); clearTimeout(timer); stop();
      window.removeEventListener(TASBEEH_CHANGED, changed); window.removeEventListener('storage', storage);
      window.removeEventListener(OFFLINE_TASBEEH_SYNCED, offlineSynced); document.removeEventListener('visibilitychange', visible);
    };
  }, [currentUserId, loading, online, error, checking, blocked]);
  return null;
}
